/**
 * Komunikaty błędów odpowiedzi API.
 *
 * API zwraca błędy w kopercie {"code":"NAZWA_KODU"} — kody transportowe wynikają
 * ze statusu HTTP (mapowane w bootstrap/app.php), kody biznesowe pochodzą
 * z enuma BusinessErrorCode. Front rozróżnia przypadki po `code`, nie po statusie.
 *
 * Rozstrzyganie w resolveErrorMessage(): najpierw `code`, potem status HTTP
 * (dla odpowiedzi bez koperty), potem kod błędu axiosa.
 */

const SERVER = 'Odpowiedź serwera'
const BUSINESS = 'Operacja niemożliwa'

// Kody transportowe — status HTTP przełożony przez API na nazwę.
const TRANSPORT_MESSAGES = {
  BAD_REQUEST: 'Nieprawidłowe wywołanie zapytania',
  UNAUTHENTICATED: 'Brak autoryzacji',
  ACCESS_DENIED: 'Działanie niedostępne',
  NOT_FOUND: 'Działanie nie istnieje',
  METHOD_NOT_ALLOWED: 'Nieprawidłowe wywołanie zapytania',
  PAYLOAD_TOO_LARGE: 'Plik jest za duży',
  SESSION_EXPIRED: 'Sesja wygasła. Zaloguj się ponownie',
  TOO_MANY_REQUESTS: 'Zbyt wiele prób. Odczekaj chwilę i spróbuj ponownie',
  SERVICE_UNAVAILABLE: 'Serwis jest chwilowo niedostępny. Spróbuj za chwilę'
}

// Kody biznesowe — BusinessErrorCode po stronie API.
// Trzy ostatnie widoki obsługują dziś same przez handleErrors: false; wpisy
// zabezpieczają nowe wywołania, które o tym zapomną.
const BUSINESS_MESSAGES = {
  PROTOCOL_ALREADY_SAVED: 'Protokół został już zapisany i nie można go zmienić',
  INVALID_STUDENT: 'Lista zawiera ucznia, którego nie można ująć w protokole',
  THRESHOLD_NOT_SET: 'Nie ustawiono progu punktowego',
  INVALID_TOKEN: 'Link jest nieprawidłowy lub stracił ważność',
  EMAIL_ALREADY_CONFIRMED: 'Ten adres email został już potwierdzony',
  EMAIL_CONFLICT: 'Ten adres email jest już zajęty',
  STUDENT_REGISTERED: 'Uczeń o tym numerze PESEL jest już zarejestrowany',
  SCHOOL_REGISTERED: 'Ta szkoła jest już zarejestrowana w systemie',
  WORK_FILE_APPROVED: 'Praca została zatwierdzona — nie można wgrać nowego pliku',
  INVALID_TEST_ACCESS_CODE: 'Nieprawidłowy kod dostępu do testu'
}

// Odpowiedzi bez koperty {"code": ...} — statusy, których API nie mapuje na kod.
const HTTP_STATUS_MESSAGES = {
  401: 'Brak autoryzacji',
  403: 'Działanie niedostępne',
  404: 'Działanie nie istnieje',
  409: 'Zapytanie nie zostało poprawnie przetworzone',
  422: 'Zapytanie nie zostało poprawnie przetworzone'
}

const CONFIG_ERROR_CODES = [
  'ERR_BAD_OPTION_VALUE',
  'ERR_BAD_OPTION',
  'ERR_NOT_SUPPORT',
  'ERR_INVALID_URL',
  'ERR_DEPRECATED'
]

const NETWORK_ERROR_CODES = [
  'ECONNABORTED',
  'ETIMEDOUT',
  'ERR_NETWORK',
  'ERR_FR_TOO_MANY_REDIRECTS',
  'ERR_CANCELED'
]

/**
 * Zamienia błąd axiosa na treść notyfikacji.
 *
 * @param {object} error błąd z interceptora axiosa
 * @returns {{summary: string, detail: string}}
 */
export function resolveErrorMessage(error) {
  const status = error?.response?.status
  const code = error?.response?.data?.code

  if (code && BUSINESS_MESSAGES[code]) {
    return { summary: BUSINESS, detail: BUSINESS_MESSAGES[code] }
  }

  if (code && TRANSPORT_MESSAGES[code]) {
    return { summary: SERVER, detail: TRANSPORT_MESSAGES[code] }
  }

  if (status) {
    // CLIENT_ERROR i SERVER_ERROR z API oraz odpowiedzi spoza kontraktu
    // (błąd serwera WWW, proxy) — komunikat generyczny ze statusem.
    const detail =
      status < 500
        ? (HTTP_STATUS_MESSAGES[status] ?? 'Zapytanie zwróciło błąd: ' + status)
        : 'Błędne działanie serwera: ' + status

    return { summary: SERVER, detail }
  }

  if (CONFIG_ERROR_CODES.includes(error?.code)) {
    return {
      summary: 'Błąd aplikacji',
      detail: 'Błędne wywołanie zapytania do serwera: ' + error.code
    }
  }

  if (NETWORK_ERROR_CODES.includes(error?.code)) {
    return {
      summary: 'Błąd komunikacji',
      detail: 'Błąd komunikacji z serwerem: ' + error.code
    }
  }

  return { summary: 'Błąd', detail: 'Wystąpił nieoczekiwany błąd w komunikacji z serwerem' }
}
